import { FormatIcu } from "@tolgee/format-icu";
import { BackendFetch, Tolgee, type TolgeeInstance } from "@tolgee/web";
import { localeFromPath, type SupportedLocale } from "#/lib/locale.ts";

import enCommon from "./messages/en/common.json";
import enLanding from "./messages/en/landing.json";
import enReferrals from "./messages/en/referrals.json";
import enTrade from "./messages/en/trade.json";
import koCommon from "./messages/ko/common.json";
import koLanding from "./messages/ko/landing.json";
import koReferrals from "./messages/ko/referrals.json";
import koTrade from "./messages/ko/trade.json";
import zhCommon from "./messages/zh/common.json";
import zhLanding from "./messages/zh/landing.json";
import zhReferrals from "./messages/zh/referrals.json";
import zhTrade from "./messages/zh/trade.json";

const cdnUrl = import.meta.env.VITE_TOLGEE_CDN_URL;
const allEnData = {
	...enCommon,
	...enLanding,
	...enReferrals,
	...enTrade,
};
const allKoData = {
	...koCommon,
	...koLanding,
	...koReferrals,
	...koTrade,
};
const allZhData = {
	...zhCommon,
	...zhLanding,
	...zhReferrals,
	...zhTrade,
};

// 서버(SSR·빌드타임 프리렌더)에서도 무조건 제공 — 없으면 TolgeeProvider가
// fallback(null)을 렌더해 문서 전체가 빈다. JSON은 어차피 정적 import라
// 클라이언트 번들 비용 변화 없음.
export const TOLGEE_STATIC_DATA = {
	en: allEnData,
	ko: allKoData,
	zh: allZhData,
};

/**
 * 인스턴스 팩토리. 서버(Workers)는 요청마다 새로 만든다(getRouter) — 아이솔레이트가
 * 요청 간 재사용되므로 싱글턴이면 한 요청의 changeLanguage가 동시 요청의 HTML 언어를
 * 바꾼다. 클라이언트는 getClientTolgee() 단일 인스턴스.
 */
export function createTolgee(language: SupportedLocale): TolgeeInstance {
	return Tolgee()
		.use(FormatIcu())
		.use(
			BackendFetch({
				prefix: cdnUrl,
				// CDN 실패 시 reject 대신 staticData로 폴백 (기본값 false는 throw → 언어 전환이 깨짐)
				fallbackOnFail: true,
			}),
		)
		.init({
			language,
			fallbackLanguage: "en",
			staticData: TOLGEE_STATIC_DATA,
		});
}

let clientTolgee: TolgeeInstance | undefined;

/**
 * 클라이언트 단일 인스턴스 — 라우터(getRouter)와 라우터 밖 비-React 코드(토스트 등)가
 * 공유한다. 초기 언어는 URL 프리픽스 ?? "en": SSR HTML과 첫 렌더가 같아야 hydration이
 * 깨지지 않는다. 저장된 선호(zh 등)는 마운트 후 LocaleSync가 적용한다.
 */
export function getClientTolgee(): TolgeeInstance {
	if (clientTolgee) return clientTolgee;
	clientTolgee = createTolgee(localeFromPath(window.location.pathname) ?? "en");
	// staticData가 캐시를 선점해 BackendFetch가 스킵되므로(strict exists), 번들로 즉시
	// 렌더한 뒤 세 언어를 CDN 최신본으로 백그라운드 갱신한다. loadRecords는 useCache
	// 없이 항상 fetch하고 캐시를 덮어쓴다 → 이후 언어 전환도 fresh. CDN URL이 없으면
	// (로컬 JSON 작업 등) 갱신을 끈다.
	if (cdnUrl) {
		void clientTolgee
			.loadRecords(
				[{ language: "en" }, { language: "ko" }, { language: "zh" }],
				{ noDev: true },
			)
			.catch(() => {});
	}
	return clientTolgee;
}
